import { Command, CommandGroup, CommandItem, CommandInput as CommandPrimitiveInput, CommandList, CommandEmpty } from 'cmdk'
import { Check, ChevronDown, X } from 'lucide-react'

import React from 'react'

import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Button } from '@/components/ui/button'

type Option = {
  value: string
  label: string
}

interface MultiSelectProps {
  options: Option[]
  value: string[]
  onChange: (value: string[]) => void
  placeholder?: string
  className?: string
  disabled?: boolean
}

export function MultiSelect({
  options,
  value = [],
  onChange,
  placeholder = 'Select options...',
  className,
  disabled = false,
}: MultiSelectProps) {
  const [open, setOpen] = React.useState(false)
  const [inputValue, setInputValue] = React.useState('')

  const selected = (value || [])
    .map((val) => options.find((opt) => opt.value === val))
    .filter(Boolean) as Option[]

  const handleSelect = (optionValue: string) => {
    const isSelected = value.includes(optionValue)
    if (isSelected) {
      onChange(value.filter((v) => v !== optionValue))
    } else {
      onChange([...value, optionValue])
    }
  }

  const handleUnselect = (optionValue: string) => {
    onChange(value.filter((val) => val !== optionValue))
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'flex min-h-8 w-full items-center justify-between rounded-md border border-input bg-card px-3 py-1 text-sm font-normal transition-[border-color,box-shadow] hover:border-grey-400 hover:bg-card focus:border-ring focus:ring-1 focus:ring-ring',
            className
          )}>
          <div className="flex flex-wrap gap-1.5 overflow-hidden">
            {selected.length > 0 ? (
              selected.map((option) => (
                <Badge
                  key={option.value}
                  variant="secondary"
                  className="group flex items-center gap-1 transition-colors hover:bg-fill-hover"
                >
                  {option.label}
                  <button
                    type="button"
                    className="ml-0.5 rounded-full outline-none ring-offset-background group-hover:bg-secondary-foreground/10"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleUnselect(option.value)
                      }
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                    }}
                    onClick={(e) => {
                      e.stopPropagation()
                      handleUnselect(option.value)
                    }}
                  >
                    <X className="h-3 w-3 text-muted-foreground transition-colors hover:text-foreground" />
                  </button>
                </Badge>
              ))
            ) : (
              <span className="text-muted-foreground/70">{placeholder}</span>
            )}
          </div>
          <ChevronDown className={cn("h-4 w-4 shrink-0 opacity-50 transition-transform duration-200", open && "rotate-180")} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 shadow-dropdown border-border/40" align="start">
        <Command className="w-full" shouldFilter={false}>
          <div className="flex items-center border-b px-3">
            <CommandPrimitiveInput
              placeholder="Search..."
              value={inputValue}
              onValueChange={setInputValue}
              className="flex h-8 w-full rounded-md bg-transparent py-1 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>
          <CommandList className="max-h-[300px] overflow-y-auto">
            <CommandEmpty className="py-6 text-center text-sm text-muted-foreground animate-in fade-in duration-200">
              No results found.
            </CommandEmpty>
            <CommandGroup className="p-1.5">
              {options
                .filter((opt) => opt.label.toLowerCase().includes(inputValue.toLowerCase()))
                .map((option) => {
                  const isSelected = value.includes(option.value)
                  return (
                    <CommandItem
                      key={option.value}
                      onSelect={() => handleSelect(option.value)}
                      className={cn(
                        "flex items-center justify-between px-2 py-2 cursor-pointer rounded-sm hover:bg-accent transition-colors",
                        isSelected && "bg-accent/40"
                      )}
                    >
                      <span className="text-sm">{option.label}</span>
                      <div className={cn(
                        "flex h-4 w-4 items-center justify-center rounded-sm border border-primary transition-all",
                        isSelected ? "bg-primary text-primary-foreground" : "opacity-50"
                      )}>
                        {isSelected && <Check className="h-3 w-3" />}
                      </div>
                    </CommandItem>
                  )
                })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
